import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ────────────────────────────────────────────────────────────────────────────
// Suprime overlay vermelho do CRA para "Script error." cross-origin opaco
// (Safari iOS dispara durante "Compartilhar > Adicionar à Tela de Início").
// ────────────────────────────────────────────────────────────────────────────
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    const isOpaqueScriptError =
      e && e.message === "Script error." && !e.filename && !e.error;
    if (isOpaqueScriptError) {
      e.preventDefault();
      e.stopImmediatePropagation?.();
      return false;
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const r = e && e.reason;
    const msg = (r && (r.message || String(r))) || "";
    if (msg === "Script error." || msg === "" || msg === "undefined") {
      e.preventDefault();
    }
  });

  // ──────────────────────────────────────────────────────────────────────
  // Diagnóstico de viewport — facilita análises de bugs intermitentes
  // de escala em mobile/PWA. Imprime no console em DEV e em PWA standalone.
  // ──────────────────────────────────────────────────────────────────────
  const logViewport = (event) => {
    const vv = window.visualViewport;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    const data = {
      event,
      screenWidth: window.screen?.width,
      screenHeight: window.screen?.height,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      docClientWidth: document.documentElement.clientWidth,
      docClientHeight: document.documentElement.clientHeight,
      vvWidth: vv?.width,
      vvHeight: vv?.height,
      vvScale: vv?.scale,
      vvOffsetTop: vv?.offsetTop,
      dpr: window.devicePixelRatio,
      orientation: window.screen?.orientation?.type,
      standalone,
    };
    try { console.info("[viewport]", data); } catch (_e) { /* ignore */ }
    try {
      // exposto para inspeção rápida no Safari Web Inspector / Chrome devtools
      window.__lastViewport = data;
    } catch (_e) { /* ignore */ }
  };
  window.addEventListener("load", () => logViewport("load"));
  window.addEventListener("orientationchange", () => setTimeout(() => logViewport("orientationchange"), 200));
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", () => logViewport("vv-resize"));
  }

  // ──────────────────────────────────────────────────────────────────────
  // Proteção definitiva contra "zoom preso" no iOS PWA
  // ──────────────────────────────────────────────────────────────────────
  // Causa: o iOS pode "lembrar" um nível de zoom de uma sessão anterior
  // ou de quando um campo de texto com font-size < 16px foi focado em
  // versão antiga do app. Quando isso ocorre, `visualViewport.scale` fica
  // diferente de 1 e a página renderiza encolhida/cortada — exatamente o
  // sintoma intermitente reportado.
  //
  // Aqui detectamos o caso APENAS em PWA standalone (onde pinch-zoom
  // não é uso esperado) e resetamos a viewport via mutação do meta tag.
  // Não fazemos isso no Safari normal — lá o pinch-zoom é legítimo.
  const isStandalonePWA = () =>
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  const resetViewportIfPWA = () => {
    if (!isStandalonePWA()) return;
    const vv = window.visualViewport;
    if (!vv) return;
    // Tolerância pequena pra evitar falsos positivos por arredondamento.
    if (Math.abs(vv.scale - 1) < 0.02) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    // Toggle temporário força o iOS a recalcular a viewport com scale=1.
    const original = meta.getAttribute("content");
    meta.setAttribute("content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover");
    setTimeout(() => {
      meta.setAttribute("content", original ||
        "width=device-width, initial-scale=1, viewport-fit=cover");
    }, 300);
    try { console.warn("[viewport] PWA scale reset:", vv.scale, "→ 1"); } catch (_e) { /* ignore */ }
  };
  // Verifica no load e ao retornar do background (visibilitychange).
  window.addEventListener("load", () => setTimeout(resetViewportIfPWA, 300));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) setTimeout(resetViewportIfPWA, 200);
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
